import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ProfileData, EventProgramParticipation } from '../types/profile.types';
import Sidebar from './Sidebar';
import Tabs from './Tabs';
import SubTabs from './SubTabs';
import BasicInfoSection from './sections/BasicInfoSection';
import ContactInfoSection from './sections/ContactInfoSection';
import ThematicAxesSection from './sections/ThematicAxesSection';
import InstitutionalDescriptionSection from './sections/InstitutionalDescriptionSection';
import SpeakersSection from './sections/SpeakersSection';
import ParallelEventsSection from './sections/ParallelEventsSection';
import InitiativesSection, { Initiative } from './sections/InitiativesSection';
import BusinessRoundsSection from './sections/BusinessRoundsSection';
import ParticipationModalitySection from './sections/ParticipationModalitySection';
import ExpoZoneSection from './sections/ExpoZoneSection';
import FormField from './FormField';
import CheckboxGroup from './CheckboxGroup';
import NetworkingReservations from './networking/NetworkingReservations';
import {
  canSeeNetworkingTab,
  NETWORKING_MAIN_TAB_ID,
  NETWORKING_MAIN_TAB_LABEL,
} from './networking/networkingCatalogReturn';
import { eventProgramParticipation } from '../data/profileData';
import { uploadToSupabase } from '../services/supabase';
import { validateFileSize, MAX_FILE_SIZE_IMAGES } from '../utils/fileValidation';
import { useFileError } from '../contexts/FileErrorContext';
import { useUploadProgress } from '../contexts/UploadProgressContext';
import { useTimeout } from '../contexts/TimeoutContext';
import { handleUploadError } from '../utils/uploadErrorHandler';
import { handleRateLimitError } from '../utils/rateLimitHandler';
import { AnalyticsEvents } from '../services/analytics';
import './ProfileForm.css';

interface ProfileFormProps {
  onSubmit: (data: ProfileData) => void;
  userEmail?: string; // Email del usuario autenticado
  initialData?: ProfileData | null; // Datos iniciales del perfil cargado desde BD
  onLogoUpload?: (logoBase64: string) => Promise<void>; // Callback para guardar logo automáticamente
  onLogoDelete?: () => Promise<void>; // Callback para eliminar logo
  onGetFormData?: (getData: () => ProfileData) => void; // Callback para exponer función que obtiene los datos del formulario
  onWantsToManageProfileChange?: (wantsToManage: boolean) => void; // Callback para notificar cambios en wantsToManageProfile
  /** Si hay cambios sin guardar, el padre muestra modales y llama a `proceed` al completar guardado o al elegir ir sin guardar. */
  beforeNavigateToNetworking?: (proceed: () => void) => void;
  /** Incrementado desde App (p. ej. popup de ingreso) para abrir la pestaña de networking. */
  openNetworkingTabSignal?: number;
}

const ProfileForm: React.FC<ProfileFormProps> = ({
  onSubmit,
  userEmail,
  initialData,
  onLogoUpload,
  onGetFormData,
  onWantsToManageProfileChange,
  beforeNavigateToNetworking,
  openNetworkingTabSignal = 0,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showFileSizeError } = useFileError();
  const { startUpload, updateProgress, finishUpload } = useUploadProgress();
  const { showTimeout } = useTimeout();
  // Estado inicial del formulario
  const getInitialFormData = (): ProfileData => {
    if (initialData) {
      // Normalizar arrays de temáticas para asegurar que siempre sean arrays válidos
      const cleanThematicAxes = (axes: any[]): any[] => {
        if (!Array.isArray(axes)) return [];
        return axes.filter((axis: any) => {
          const axisStr = String(axis);
          return axisStr && 
            axisStr.trim() !== '' && 
            axisStr !== '[' && 
            axisStr !== ']' &&
            !axisStr.startsWith('{') &&
            !axisStr.startsWith('"[');
        });
      };

      const normalizedData = {
        ...initialData,
        basicInfo: {
          ...initialData.basicInfo,
          thematicAxes: cleanThematicAxes(initialData.basicInfo?.thematicAxes || [])
        },
        contactInfo: {
          ...initialData.contactInfo,
          thematicAxes: cleanThematicAxes(initialData.contactInfo?.thematicAxes || [])
        }
      };
      return normalizedData;
    }
    // Si no hay datos iniciales, usar valores por defecto
    return {
    basicInfo: {
      organizationName: '',
      organizationType: '',
      country: '',
      website: '',
      industries: [],
      eventProgramParticipation: [],
      socialNetworks: {}
    },
      contactInfo: {
        firstName: '',
        lastName: '',
        position: '',
        email: userEmail || '',
        phone: '',
        codigoPais: 'AR',
        numeroTelefono: '',
        area: '',
        subarea: '',
        wantsToManageProfile: false,
        wantsToParticipateBusinessRounds: false
      },
    catalogSections: [],
    institutionalDescription: {
      description: ''
    },
    initiatives: [],
            participationModalities: [],
            selectedStands: [],
            confirmedStands: [],
            speakers: [],
            businessRoundsParticipants: [],
            parallelEvents: []
    };
  };

  const [formData, setFormData] = useState<ProfileData>(getInitialFormData());
  const formDataRef = useRef<ProfileData>(formData);
  const hasInitialDataLoaded = useRef<boolean>(false);
  /** Evita re-sincronizar cuando el padre pasa un nuevo objeto con el mismo contenido (p. ej. tras guardar) → sin pestañeo */
  const lastInitialSyncHashRef = useRef<string>('');

  const buildInitialSyncHash = (d: ProfileData | null | undefined): string => {
    if (!d) return '';
    try {
      const logo = d.basicInfo?.logo;
      const logoKey =
        typeof logo === 'string' ? logo.slice(0, 400) : logo instanceof File ? `file:${logo.name}` : '';
      const sp = (d.speakers || []).map((s: any) => ({
        bio: (s?.biography ?? '').slice(0, 120),
        li: s?.linkedin ?? '',
        ig: s?.instagram ?? '',
        tw: s?.twitter ?? ''
      }));
      const pe = (d.parallelEvents || []).map((ev: any) =>
        (ev?.localSpeakers || []).map((ls: any) => ({
          bio: (ls?.biography ?? '').slice(0, 120),
          li: ls?.linkedin ?? '',
          ig: ls?.instagram ?? '',
          tw: ls?.twitter ?? ''
        }))
      );
      const ini = (d.initiatives || []).map((i: any) => ({
        em: i?.contactEmail ?? '',
        nt: i?.contactNumeroTelefono ?? '',
        ph: i?.contactPhone ?? ''
      }));
      const br = (d.businessRoundsParticipants || []).map((p: any) => ({
        em: p?.email ?? '',
        nt: p?.numeroTelefono ?? '',
        ph: p?.phone ?? ''
      }));
      return JSON.stringify({
        nt: d.contactInfo?.numeroTelefono ?? '',
        ph: d.contactInfo?.phone ?? '',
        cp: d.contactInfo?.codigoPais ?? '',
        web: d.basicInfo?.website ?? '',
        sn: d.basicInfo?.socialNetworks || {},
        logoKey,
        sp,
        pe,
        ini,
        br
      });
    } catch {
      return String(Math.random());
    }
  };

  // Mantener el ref actualizado con los datos del formulario
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Estado para pestañas principales y subpestañas
  const [activeMainTab, setActiveMainTab] = useState('personal');
  const [activeSubTab, setActiveSubTab] = useState('');
  // En móvil/tablet: la clase checkbox-container-visible solo se aplica cuando la barra de checkboxes está en viewport
  const [checkboxBarVisible, setCheckboxBarVisible] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1025);
  // Estado para controlar si el sidebar está abierto o cerrado (persistir en localStorage)
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarOpen');
    return saved !== null ? saved === 'true' : true;
  });
  // Errores vacíos ya que la validación se hace al guardar todo el perfil
  const errors: Record<string, any> = {};

  // Función para toggle del sidebar
  const handleSidebarToggle = () => {
    const newState = !isSidebarOpen;
    setIsSidebarOpen(newState);
    localStorage.setItem('sidebarOpen', String(newState));
  };

  // Cargar datos iniciales SOLO la primera vez que estén disponibles
  // Esto evita que se resetee el formulario cuando initialData cambia después
  useEffect(() => {
    // Solo cargar si initialData existe Y no se ha cargado antes
    if (initialData && !hasInitialDataLoaded.current) {
      // Normalizar arrays de temáticas para asegurar que siempre sean arrays válidos
      const cleanThematicAxes = (axes: any[]): any[] => {
        if (!Array.isArray(axes)) return [];
        return axes.filter((axis: any) => {
          const axisStr = String(axis);
          return axisStr && 
            axisStr.trim() !== '' && 
            axisStr !== '[' && 
            axisStr !== ']' &&
            !axisStr.startsWith('{') &&
            !axisStr.startsWith('"[');
        });
      };

      // Normalizar catalogSections por si viene como string JSON
      const normalizeCatalogSections = (sections: any): any[] => {
        if (!sections) return [];
        if (Array.isArray(sections)) return sections;
        if (typeof sections === 'string') {
          try {
            const parsed = JSON.parse(sections);
            return Array.isArray(parsed) ? parsed : [];
          } catch (e) {
            return [];
          }
        }
        return [];
      };

      const normalizedData = {
        ...initialData,
        catalogSections: normalizeCatalogSections(initialData.catalogSections),
        basicInfo: {
          ...initialData.basicInfo,
          thematicAxes: cleanThematicAxes(initialData.basicInfo?.thematicAxes || [])
        },
        contactInfo: {
          ...initialData.contactInfo,
          thematicAxes: cleanThematicAxes(initialData.contactInfo?.thematicAxes || [])
        }
      };
      setFormData(normalizedData);
      hasInitialDataLoaded.current = true;
      lastInitialSyncHashRef.current = buildInitialSyncHash(normalizedData);
    } else if (initialData && hasInitialDataLoaded.current) {
      const nextHash = buildInitialSyncHash(initialData);
      if (nextHash === lastInitialSyncHashRef.current) {
        return;
      }
      lastInitialSyncHashRef.current = nextHash;
      // Si initialData cambia después de la primera carga, actualizar campos específicos
      // Actualizar el logo si cambió (incluyendo cuando se elimina)
      // Y también actualizar campos que fueron vaciados intencionalmente
      setFormData(prev => {
        let updated = { ...prev };
        let hasChanges = false;

        // Actualizar logo si cambió, pero solo si el logo actual no es una URL válida de Supabase
        // Esto evita que se sobrescriba el logo nuevo con el logo antiguo de la BD después de guardar
        const currentLogo = prev?.basicInfo?.logo;
        const newLogo = initialData?.basicInfo?.logo;
        const currentLogoValue = currentLogo && typeof currentLogo === 'string' && currentLogo.trim() !== '' ? currentLogo : undefined;
        const newLogoValue = newLogo && typeof newLogo === 'string' && newLogo.trim() !== '' ? newLogo : undefined;

        // Verificar si el logo actual es una URL válida de Supabase o una data URL
        const isCurrentLogoValid = currentLogoValue && (
          currentLogoValue.startsWith('http://') || 
          currentLogoValue.startsWith('https://') || 
          currentLogoValue.startsWith('data:image/')
        );

        // Solo actualizar el logo si:
        // 1. El logo cambió Y
        // 2. El logo actual NO es una URL válida (para preservar logos nuevos subidos) O
        // 3. El nuevo logo es null/undefined (para permitir eliminar el logo)
        if (currentLogoValue !== newLogoValue && (!isCurrentLogoValid || !newLogoValue)) {
          updated = {
            ...updated,
            basicInfo: {
              ...updated.basicInfo,
              logo: newLogoValue
            }
          };
          hasChanges = true;
        }

        // Actualizar campos de contacto si cambiaron (especialmente si se vaciaron)
        if (initialData.contactInfo) {
          if (!updated.contactInfo) updated.contactInfo = {} as any;
          
          // Teléfono - actualizar tanto numeroTelefono como phone
          // CRÍTICO: Usar comparación estricta y normalizar valores undefined/null a ''
          const newNumeroTelefono = initialData.contactInfo.numeroTelefono ?? '';
          const currentNumeroTelefono = updated.contactInfo.numeroTelefono ?? '';
          if (newNumeroTelefono !== currentNumeroTelefono) {
            updated.contactInfo.numeroTelefono = newNumeroTelefono;
            hasChanges = true;
          }
          // CRÍTICO: También actualizar phone cuando cambia (especialmente cuando se vacía)
          const newPhone = initialData.contactInfo.phone ?? '';
          const currentPhone = updated.contactInfo.phone ?? '';
          if (newPhone !== currentPhone) {
            updated.contactInfo.phone = newPhone;
            hasChanges = true;
          }
          // CRÍTICO: También actualizar codigoPais si cambia
          if (initialData.contactInfo.codigoPais !== updated.contactInfo.codigoPais) {
            updated.contactInfo.codigoPais = initialData.contactInfo.codigoPais || 'AR';
            hasChanges = true;
          }
        }

        // Actualizar sitio web
        if (initialData.basicInfo?.website !== updated.basicInfo?.website) {
          if (!updated.basicInfo) updated.basicInfo = {} as any;
          updated.basicInfo.website = initialData.basicInfo.website || '';
          hasChanges = true;
        }

        // Actualizar redes sociales
        if (initialData.basicInfo?.socialNetworks) {
          if (!updated.basicInfo) updated.basicInfo = {} as any;
          if (!updated.basicInfo.socialNetworks) updated.basicInfo.socialNetworks = {} as any;
          
          const socialFields = ['facebook', 'twitter', 'linkedin', 'instagram', 'youtube', 'other'] as const;
          socialFields.forEach(field => {
            const newValue = initialData.basicInfo.socialNetworks?.[field] || '';
            const currentValue = updated.basicInfo.socialNetworks?.[field] || '';
            if (newValue !== currentValue && updated.basicInfo.socialNetworks) {
              updated.basicInfo.socialNetworks[field] = newValue;
              hasChanges = true;
            }
          });
        }

        // Actualizar oradores
        if (initialData.speakers && Array.isArray(initialData.speakers)) {
          // CRÍTICO: Crear una nueva referencia del array para forzar la actualización
          const updatedSpeakers = [...(updated.speakers || [])];
          let speakersChanged = false;
          
          initialData.speakers.forEach((speaker, index) => {
            // Asegurar que existe el orador en el array actualizado
            if (!updatedSpeakers[index]) {
              updatedSpeakers[index] = { ...speaker } as any;
              speakersChanged = true;
            } else {
              // Crear una nueva referencia del objeto orador
              const updatedSpeaker = { ...updatedSpeakers[index] };
              let speakerChanged = false;
              
              const fieldsToUpdate = ['biography', 'linkedin', 'instagram', 'twitter'] as const;
              fieldsToUpdate.forEach(field => {
                // CRÍTICO: Usar comparación estricta y normalizar valores undefined/null a ''
                const newValue = speaker[field] ?? '';
                const currentValue = updatedSpeaker[field] ?? '';
                if (newValue !== currentValue) {
                  updatedSpeaker[field] = newValue;
                  speakerChanged = true;
                }
              });
              
              if (speakerChanged) {
                updatedSpeakers[index] = updatedSpeaker;
                speakersChanged = true;
              }
            }
          });
          
          // CRÍTICO: Si hubo cambios, actualizar el array completo
          if (speakersChanged || updatedSpeakers.length !== (updated.speakers?.length || 0)) {
            updated.speakers = updatedSpeakers;
            hasChanges = true;
          }
        }

        // Actualizar oradores en eventos paralelos
        if (initialData.parallelEvents && Array.isArray(initialData.parallelEvents)) {
          if (!updated.parallelEvents) updated.parallelEvents = [];
          
          initialData.parallelEvents.forEach((event, eventIndex) => {
            if (!updated.parallelEvents) updated.parallelEvents = [];
            if (!updated.parallelEvents[eventIndex]) updated.parallelEvents[eventIndex] = {} as any;
            if (event.localSpeakers && Array.isArray(event.localSpeakers)) {
              if (!updated.parallelEvents[eventIndex].localSpeakers) updated.parallelEvents[eventIndex].localSpeakers = [];
              
              event.localSpeakers.forEach((speaker, speakerIndex) => {
                if (!updated.parallelEvents || !updated.parallelEvents[eventIndex] || !updated.parallelEvents[eventIndex].localSpeakers) return;
                if (!updated.parallelEvents[eventIndex].localSpeakers![speakerIndex]) {
                  updated.parallelEvents[eventIndex].localSpeakers![speakerIndex] = {} as any;
                }
                
                const fieldsToUpdate = ['biography', 'linkedin', 'instagram', 'twitter'] as const;
                fieldsToUpdate.forEach(field => {
                  if (!updated.parallelEvents || !updated.parallelEvents[eventIndex] || !updated.parallelEvents[eventIndex].localSpeakers) return;
                  const newValue = speaker[field] || '';
                  const currentValue = updated.parallelEvents[eventIndex].localSpeakers![speakerIndex][field] || '';
                  if (newValue !== currentValue) {
                    updated.parallelEvents[eventIndex].localSpeakers![speakerIndex][field] = newValue;
                    hasChanges = true;
                  }
                });
              });
            }
          });
        }

        // Actualizar iniciativas
        if (initialData.initiatives && Array.isArray(initialData.initiatives)) {
          if (!updated.initiatives) updated.initiatives = [];
          
          initialData.initiatives.forEach((initiative, index) => {
            if (!updated.initiatives[index]) updated.initiatives[index] = {} as any;
            
            if (initiative.contactEmail !== updated.initiatives[index].contactEmail) {
              updated.initiatives[index].contactEmail = initiative.contactEmail || '';
              hasChanges = true;
            }
            if (initiative.contactNumeroTelefono !== updated.initiatives[index].contactNumeroTelefono) {
              updated.initiatives[index].contactNumeroTelefono = initiative.contactNumeroTelefono || '';
              hasChanges = true;
            }
            // CRÍTICO: También actualizar contactPhone cuando cambia (especialmente cuando se vacía)
            if (initiative.contactPhone !== updated.initiatives[index].contactPhone) {
              updated.initiatives[index].contactPhone = initiative.contactPhone || '';
              hasChanges = true;
            }
          });
        }

        // Actualizar participantes de rondas
        if (initialData.businessRoundsParticipants && Array.isArray(initialData.businessRoundsParticipants)) {
          if (!updated.businessRoundsParticipants) updated.businessRoundsParticipants = [];
          
          initialData.businessRoundsParticipants.forEach((participant, index) => {
            if (!updated.businessRoundsParticipants) updated.businessRoundsParticipants = [];
            if (!updated.businessRoundsParticipants[index]) updated.businessRoundsParticipants[index] = {} as any;
            
            if (participant.email !== updated.businessRoundsParticipants[index]?.email) {
              if (updated.businessRoundsParticipants[index]) {
                updated.businessRoundsParticipants[index].email = participant.email || '';
                hasChanges = true;
              }
            }
            if (participant.numeroTelefono !== updated.businessRoundsParticipants[index]?.numeroTelefono) {
              if (updated.businessRoundsParticipants[index]) {
                updated.businessRoundsParticipants[index].numeroTelefono = participant.numeroTelefono || '';
                hasChanges = true;
              }
            }
            // CRÍTICO: También actualizar phone cuando cambia (especialmente cuando se vacía)
            if (participant.phone !== updated.businessRoundsParticipants[index]?.phone) {
              if (updated.businessRoundsParticipants[index]) {
                updated.businessRoundsParticipants[index].phone = participant.phone || '';
                hasChanges = true;
              }
            }
          });
        }

        if (!hasChanges) return prev;
        return updated;
      });
    }
  }, [initialData]);

  // Sincronizar email del usuario con el formulario
  useEffect(() => {
    if (userEmail) {
      setFormData(prev => ({
        ...prev,
        contactInfo: {
          ...prev.contactInfo,
          email: userEmail
        }
      }));
    }
  }, [userEmail]);

  // Exponer función para obtener los datos del formulario
  // Este efecto se ejecuta cuando onGetFormData está disponible
  useEffect(() => {
    if (onGetFormData) {
      // Crear una función estable que siempre devuelva los datos actuales
      const getDataFunction = () => {
        return formDataRef.current;
      };
      // Pasar la función directamente
      onGetFormData(getDataFunction);
    } else {
    }
  }, [onGetFormData]); // Solo dependemos de onGetFormData

  const showNetworkingTab = useMemo(() => canSeeNetworkingTab(userEmail), [userEmail]);

  useEffect(() => {
    if (searchParams.get('tab') !== NETWORKING_MAIN_TAB_ID || !showNetworkingTab) return;
    setActiveMainTab(NETWORKING_MAIN_TAB_ID);
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, showNetworkingTab]);

  // Verificar si el usuario quiere administrar el perfil organizacional
  const wantsToManageProfile = formData.contactInfo.wantsToManageProfile || false;

  // Notificar cambios en wantsToManageProfile de manera reactiva
  useEffect(() => {
    if (onWantsToManageProfileChange) {
      onWantsToManageProfileChange(wantsToManageProfile);
    }
  }, [wantsToManageProfile, onWantsToManageProfileChange]);

  useEffect(() => {
    if (!showNetworkingTab && activeMainTab === 'networking') {
      setActiveMainTab('personal');
      setActiveSubTab('');
    }
  }, [showNetworkingTab, activeMainTab]);

  // Lógica para el layout del Sidebar:
  // - Si está en "Perfil Personal", usar layout de desmarcado (false)
  // - Solo en "Perfil Institucional": layout con logo institucional
  const sidebarLayoutWantsToManage = activeMainTab === 'institutional';

  // Si se desmarca el checkbox, cambiar automáticamente a la pestaña "personal"
  useEffect(() => {
    if (!wantsToManageProfile && activeMainTab !== 'personal' && activeMainTab !== 'networking') {
      setActiveMainTab('personal');
      setActiveSubTab(''); // Sin subpestañas en Perfil Personal
    }
  }, [wantsToManageProfile, activeMainTab]);

  // Pestañas principales
  const mainTabs = [
    { id: 'personal', label: 'Perfil Personal' },
    ...(wantsToManageProfile ? [
      { id: 'institutional', label: 'Perfil Institucional' }
    ] : []),
    ...(showNetworkingTab ? [{ id: NETWORKING_MAIN_TAB_ID, label: NETWORKING_MAIN_TAB_LABEL }] : []),
  ];

  // Función para obtener las subpestañas según la pestaña principal
  const getSubTabsForMainTab = (mainTabId: string) => {
    switch (mainTabId) {
      case 'personal':
        // Perfil Personal no tiene subpestañas, el contenido va directo
        return [];
      case 'institutional':
        return [
          { id: 'organization', label: 'Información de la organización' },
          { id: 'participation', label: 'Modalidad de participación' },
          { id: 'expo-zone', label: 'Zona Expo' },
          { id: 'speakers', label: 'Oradores' },
          { id: 'parallel-events', label: 'Eventos Paralelos' },
          { id: 'initiatives', label: 'Catálogo / Rondas de negocios' }
        ];
      case 'networking':
        return [];
      default:
        return [];
    }
  };

  // Actualizar subpestaña cuando cambia la pestaña principal
  useEffect(() => {
    const subTabs = getSubTabsForMainTab(activeMainTab);
    if (activeMainTab === 'personal' || activeMainTab === 'networking') {
      setActiveSubTab('');
    } else {
      // Para Perfil Institucional, seleccionar la primera subpestaña si no hay una activa
      const firstSubTab = subTabs[0]?.id;
      if (firstSubTab && !subTabs.find(st => st.id === activeSubTab)) {
        setActiveSubTab(firstSubTab);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMainTab, wantsToManageProfile]);

  // Actualizar isDesktop en resize
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1025px)');
    const handleChange = () => setIsDesktop(mql.matches);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  // Clases al body: profile-personal-active siempre en Perfil Personal; checkbox-container-visible solo cuando aplica
  // Desktop: siempre que esté en personal (barra fija visible). Móvil/tablet: solo cuando la barra entra en viewport
  useEffect(() => {
    if (activeMainTab !== 'personal') {
      setCheckboxBarVisible(false);
      document.body.classList.remove('profile-personal-active');
      document.body.classList.remove('checkbox-container-visible');
      document.documentElement.style.removeProperty('--checkbox-container-bottom');
      return;
    }
    document.body.classList.add('profile-personal-active');
    const showCheckboxOffset = isDesktop || checkboxBarVisible;
    if (showCheckboxOffset) {
      document.body.classList.add('checkbox-container-visible');
      document.documentElement.style.setProperty('--checkbox-container-bottom', '70px');
    } else {
      document.body.classList.remove('checkbox-container-visible');
      document.documentElement.style.removeProperty('--checkbox-container-bottom');
    }
    return () => {
      document.body.classList.remove('profile-personal-active');
      document.body.classList.remove('checkbox-container-visible');
      document.documentElement.style.removeProperty('--checkbox-container-bottom');
    };
  }, [activeMainTab, isDesktop, checkboxBarVisible]);

  const handleLogoDelete = async () => {
    // Limpiar el logo del estado del formulario (preview)
    // La eliminación definitiva se hará cuando se guarde el perfil
    // Usar undefined explícitamente, igual que en los oradores
    setFormData(prev => ({
      ...prev,
      basicInfo: {
        ...prev.basicInfo,
        logo: undefined
      }
    }));
  };

  const handleLogoChange = async (file: File) => {
    // Validar tipo de archivo - solo PNG y formatos vectoriales editables (sin PDF)
    const allowedTypes = [
      'image/png',
      'image/svg+xml',
      'image/svg',
      'application/postscript',
      'application/illustrator'
    ];
    const allowedExtensions = ['.png', '.svg', '.ai', '.eps'];

    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const isValidType = allowedTypes.includes(file.type) || 
                        allowedExtensions.includes(fileExtension) ||
                        file.name.toLowerCase().endsWith('.svg') ||
                        file.name.toLowerCase().endsWith('.ai') ||
                        file.name.toLowerCase().endsWith('.eps');

    if (!isValidType) {
      showFileSizeError('El logo debe estar en formato PNG o formato de curvas editable (SVG, AI, EPS).', true);
      return;
    }

    // Validar tamaño de logo antes de subirlo (usa límite de imágenes: 15MB)
    const validation = validateFileSize(file, MAX_FILE_SIZE_IMAGES, 'image');
    if (!validation.valid) {
      showFileSizeError(validation.error || 'El logo excede el tamaño máximo permitido.');
      return;
    }

    // Guardar el logo actual antes de actualizar (para poder revertir si falla)
    const previousLogo = formData.basicInfo.logo;

    try {

      // Mostrar preview inmediato usando FileReader mientras se sube
      const reader = new FileReader();
      reader.onloadend = () => {
        const previewUrl = reader.result as string;
        // Actualizar estado con preview temporal
        setFormData(prev => ({
          ...prev,
          basicInfo: {
            ...prev.basicInfo,
            logo: previewUrl // Preview temporal mientras se sube
          }
        }));
      };
      reader.readAsDataURL(file);

      // Iniciar progreso de subida

      startUpload(file.name, 1);

      try {
        // Subir a Supabase

        const logoUrl = await uploadToSupabase(file, {
          folder: 'perfil-institucional/logos',
          onProgress: (progress) => {
            updateProgress(progress, 0);
          }
        });

        // Finalizar progreso
        finishUpload();

        // Log para debugging

        // Actualizar el estado del formulario con la URL de Supabase
        setFormData(prev => {
          const updated = {
            ...prev,
            basicInfo: {
              ...prev.basicInfo,
              logo: logoUrl
            }
          };
          return updated;
        });
      } catch (error) {

        finishUpload();
        throw error; // Re-lanzar para que se maneje arriba
      }
    } catch (error: any) {
      // Finalizar progreso incluso si hay error
      finishUpload();
      
      // Verificar si es un error de rate limiting (429)
      if (handleRateLimitError(error)) {
        setFormData(prev => ({
          ...prev,
          basicInfo: {
            ...prev.basicInfo,
            logo: previousLogo || undefined
          }
        }));
        return;
      }
      
      // Verificar si es un error de formato o tamaño del backend
      if (handleUploadError(error, showFileSizeError)) {
        setFormData(prev => ({
          ...prev,
          basicInfo: {
            ...prev.basicInfo,
            logo: previousLogo || undefined
          }
        }));
        return;
      }
      
      // Verificar si es un error de timeout
      if (error?.isTimeout) {
        showTimeout(error.operationName || 'la subida del logo');
        setFormData(prev => ({
          ...prev,
          basicInfo: {
            ...prev.basicInfo,
            logo: previousLogo || undefined
          }
        }));
        return;
      }
      
      // Revertir el cambio silenciosamente para otros errores
      setFormData(prev => ({
        ...prev,
        basicInfo: {
          ...prev.basicInfo,
          logo: previousLogo || undefined
        }
      }));
    }
  };

  // Validar solo la subpestaña actual

  // Renderizar contenido según la pestaña principal y subpestaña
  const renderContent = () => {
    // Si está en Perfil Personal, mostrar el contenido directamente (sin subpestañas)
    if (activeMainTab === 'personal') {
      return (
        <>
          <ContactInfoSection
            data={formData.contactInfo}
            onChange={(data) => {
              setFormData(prev => {
                const newData = { ...prev, contactInfo: data };
                return newData;
              });
            }}
            basicInfo={formData.basicInfo}
            onBasicInfoChange={(data) => {
              setFormData(prev => ({ ...prev, basicInfo: data }));
            }}
            errors={errors.contactInfo}
            basicInfoErrors={errors.basicInfo}
            axesError={errors.thematicAxes}
            userEmail={userEmail}
            onCheckboxBarVisible={setCheckboxBarVisible}
          />
        </>
      );
    }

    if (activeMainTab === 'networking') {
      if (!showNetworkingTab) return null;
      return <NetworkingReservations userEmail={userEmail} />;
    }

    // Si está en Perfil Institucional, renderizar según la subpestaña
    return renderSubTabContent(activeSubTab);
  };

  // Renderizar contenido de subpestañas (solo para Perfil Institucional)
  const renderSubTabContent = (subTabId: string) => {
    switch (subTabId) {
      case 'organization':
        return (
          <>
            <BasicInfoSection
              data={formData.basicInfo}
              onChange={(data) => setFormData(prev => ({ ...prev, basicInfo: data }))}
              errors={errors.basicInfo}
              institutionalDescription={formData.institutionalDescription}
              onInstitutionalDescriptionChange={(data) => setFormData(prev => ({ ...prev, institutionalDescription: data }))}
              institutionalDescriptionErrors={errors.institutionalDescription}
            />
            <ThematicAxesSection
              selectedAxes={formData.basicInfo.thematicAxes || []}
              onChange={(axes) => {
                setFormData(prev => {
                  const newData = { 
                    ...prev, 
                    basicInfo: { ...prev.basicInfo, thematicAxes: axes }
                  };
                  return newData;
                });
              }}
              error={errors.thematicAxes}
            />
          </>
        );
      case 'participation':
        return (
          <>
            <ParticipationModalitySection
              selectedModalities={formData.participationModalities}
              onChange={(modalities) => setFormData({ ...formData, participationModalities: modalities })}
              agreedModality={formData.agreedModality}
              modalityObservations={formData.modalityObservations}
              error={errors.participationModalities}
              eventProgramParticipation={formData.basicInfo.eventProgramParticipation || []}
              onEventProgramParticipationChange={(values) => setFormData(prev => ({
                ...prev,
                basicInfo: {
                  ...prev.basicInfo,
                  eventProgramParticipation: values as EventProgramParticipation[]
                }
              }))}
              eventProgramParticipationError={errors.basicInfo?.eventProgramParticipation}
              requestInformation={formData.requestInformation}
              onRequestInformationChange={(value) => setFormData({ ...formData, requestInformation: value })}
            />
          </>
        );
      case 'expo-zone':

        // CRÍTICO: Asegurar que selectedStandsUpdated sea siempre un array
        let standsArray: string[] = [];
        const standsToUse = formData.selectedStandsUpdated;
        if (Array.isArray(standsToUse)) {
          standsArray = standsToUse;
        } else if (standsToUse) {
          // Si no es un array, intentar parsearlo
          if (typeof standsToUse === 'string') {
            try {
              const parsed = JSON.parse(standsToUse);
              standsArray = Array.isArray(parsed) ? parsed : [];
            } catch {
              standsArray = [];
            }
          } else {
            standsArray = [];
          }
        }

        try {
        return (
          <>
            <ExpoZoneSection
                selectedStands={standsArray}
                confirmedStands={formData.confirmedStands || []}
                onChange={(stands) => {
                  setFormData({ ...formData, selectedStandsUpdated: stands });
                }}
                requestInformation={formData.requestExpoInformation}
                onRequestInformationChange={(value) => setFormData({ ...formData, requestExpoInformation: value })}
            />
          </>
        );
        } catch (error) {
          return (
            <div style={{ padding: '20px', color: '#CE2B15' }}>
              <p>Error al cargar la sección de Zona Expo. Por favor, recarga la página.</p>
              <p style={{ fontSize: '12px', marginTop: '10px' }}>
                Error: {error instanceof Error ? error.message : 'Error desconocido'}
              </p>
            </div>
          );
        }
      case 'speakers':
        return (
          <>
            <SpeakersSection
              speakers={formData.speakers || []}
              onChange={(speakers) => {
                // Usar función de actualización para garantizar que siempre usamos el estado más reciente
                setFormData((prevFormData) => {
                  // Detectar qué oradores fueron eliminados
                  const previousSpeakerIds = new Set((prevFormData.speakers || []).map(s => s.id));
                  const newSpeakerIds = new Set(speakers.map(s => s.id));
                  const deletedSpeakerIds = Array.from(previousSpeakerIds).filter(id => !newSpeakerIds.has(id));

                  // Si se eliminaron oradores, limpiar sus referencias en eventos paralelos
                  let updatedParallelEvents = prevFormData.parallelEvents || [];
                  if (deletedSpeakerIds.length > 0 && updatedParallelEvents.length > 0) {
                    updatedParallelEvents = updatedParallelEvents.map(event => {
                      // Limpiar speakerIds (array)
                      const updatedSpeakerIds = event.speakerIds?.filter(id => !deletedSpeakerIds.includes(id));
                      // Limpiar speakerId (string, compatibilidad antigua)
                      const updatedSpeakerId = event.speakerId && !deletedSpeakerIds.includes(event.speakerId) 
                        ? event.speakerId 
                        : undefined;

                      return {
                        ...event,
                        speakerIds: updatedSpeakerIds && updatedSpeakerIds.length > 0 ? updatedSpeakerIds : undefined,
                        speakerId: updatedSpeakerId
                      };
                    });
                  }

                  return {
                    ...prevFormData,
                    speakers,
                    parallelEvents: updatedParallelEvents
                  };
                });
              }}
            />
          </>
        );
      case 'parallel-events':
        return (
          <>
            <ParallelEventsSection
              parallelEvents={formData.parallelEvents || []}
              speakers={formData.speakers || []}
              onChange={(events) => {
                // Usar función de actualización para garantizar que siempre usamos el estado más reciente
                setFormData((prevFormData) => ({
                  ...prevFormData,
                  parallelEvents: events
                }));
              }}
            />
          </>
        );
      case 'initiatives':
        return (
          <>
            <InitiativesSection
              initiatives={(formData.initiatives || []) as any}
              catalogSections={formData.catalogSections || []}
              onInitiativesChange={(initiatives) => {
                setFormData((prev) => ({
                  ...prev,
                  initiatives: initiatives as any
                }));
              }}
              onCatalogSectionsChange={(sections) => {
                setFormData((prev) => {
                  return { ...prev, catalogSections: sections };
                });
              }}
            />
            <div style={{ marginTop: '60px' }}>
              <BusinessRoundsSection
                participants={formData.businessRoundsParticipants || []}
                onChange={(participants) => {
                  setFormData((prev) => ({
                    ...prev,
                    businessRoundsParticipants: participants
                  }));
                }}
              />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const commitMainTabChange = (tabId: string, previousTab: string) => {
    setActiveMainTab(tabId);
    if (previousTab !== tabId) {
      const tabLabels: { [key: string]: string } = {
        personal: 'Perfil Personal',
        institutional: 'Perfil Institucional',
        networking: NETWORKING_MAIN_TAB_LABEL,
      };
      AnalyticsEvents.tabChange(
        tabLabels[previousTab] || previousTab,
        tabLabels[tabId] || tabId
      );
    }
    if (tabId === 'personal' || tabId === 'networking') {
      setActiveSubTab('');
    } else {
      const subTabs = getSubTabsForMainTab(tabId);
      if (subTabs.length > 0) {
        setActiveSubTab(subTabs[0].id);
      }
    }
  };

  // Manejar cambio de pestaña principal (Networking: si hay cambios sin guardar, el padre muestra modales de guardado)
  const handleMainTabChange = (tabId: string) => {
    const previousTab = activeMainTab;
    if (
      tabId === 'networking' &&
      tabId !== previousTab &&
      beforeNavigateToNetworking
    ) {
      beforeNavigateToNetworking(() => {
        commitMainTabChange('networking', previousTab);
      });
      return;
    }
    commitMainTabChange(tabId, previousTab);
  };

  const processedNetworkingOpenRef = useRef(0);
  useEffect(() => {
    if (!openNetworkingTabSignal || openNetworkingTabSignal <= processedNetworkingOpenRef.current) {
      return;
    }
    if (!showNetworkingTab) return;
    processedNetworkingOpenRef.current = openNetworkingTabSignal;

    const previousTab = activeMainTab;
    const proceed = () => commitMainTabChange(NETWORKING_MAIN_TAB_ID, previousTab);
    if (beforeNavigateToNetworking) {
      beforeNavigateToNetworking(proceed);
    } else {
      proceed();
    }
  }, [
    openNetworkingTabSignal,
    showNetworkingTab,
    activeMainTab,
    beforeNavigateToNetworking,
  ]);

  return (
    <div className="profile-form-container">
      <div className="profile-form-layout">
        <Sidebar 
          profileData={formData} 
          onLogoChange={handleLogoChange}
          onLogoDelete={handleLogoDelete}
          isOpen={isSidebarOpen}
          showLogo={activeMainTab === 'institutional' && wantsToManageProfile}
          wantsToManageProfile={sidebarLayoutWantsToManage}
        />

        {/* Botón de toggle del sidebar */}
        <button
          className={`sidebar-toggle ${isSidebarOpen ? 'open' : 'closed'}`}
          onClick={handleSidebarToggle}
          aria-label={isSidebarOpen ? 'Cerrar sidebar' : 'Abrir sidebar'}
        >
          <span className="toggle-arrow">{isSidebarOpen ? '‹' : '›'}</span>
        </button>

        <div className={`form-content-area ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
          <div className="profile-form">
            {/* Sistema de pestañas y subpestañas */}
            <Tabs
              tabs={mainTabs}
              activeTab={activeMainTab}
              onTabChange={handleMainTabChange}
            >
              {activeMainTab === 'personal' || activeMainTab === 'networking' ? (
                // Perfil Personal / Networking: sin subpestañas
                renderContent()
              ) : (
                // Perfil Institucional: mostrar con subpestañas
                <SubTabs
                  subTabs={getSubTabsForMainTab(activeMainTab)}
                  activeSubTab={activeSubTab}
                  onSubTabChange={setActiveSubTab}
                >
                  {renderContent()}
                </SubTabs>
              )}
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileForm;
